import {
  Box,
  IconButton,
  InputBase,
  ToggleButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import {
  API_DELETE,
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import useFetch from '../../api/useFetch';
import { tokens } from '../../global/theme/tokens';
import usePersistedState from '../../global/usePersistedState';
import Feedback from '../../components/special/Feedback';
import Pagination from '../../components/special/Pagination';
import SortSelect from '../../components/select/SortSelect';
import ExoProcessing from '../../components/exo/ExoProcessing';
import ExoAlert from '../../components/exo/ExoAlert';
import { ExoSkeleton } from '../../components/exo/ExoSkeleton';
import WPEToDoElementPopup from '../../components/exo/project/work-package-elements/WPEToDoElementPopup';
import { applyTaskStatsDelta } from '../../components/exo/project/task-view/overview/overviewStats';
import CapacityProject from '../../components/exo/project/capacity/CapacityProject';
import { buildCapacityProjectsQuery } from '../../components/exo/project/capacity/capacityStats';
import TaskPool from '../../components/exo/project/task-pool/TaskPool';
import MyWorkOverview from './MyWorkOverview';
import {
  addTaskToProject,
  overviewStats,
  OWN_PROJECTS_PATH,
  POOL_PROJECTS_PATH,
  projectTaskStats,
  removeTaskFromProject,
} from './poolingStats';

// a project row carries its tasks, so a page stays short
const PER_PAGE_OPTIONS = [10, 20, 30];
const PROJECTS_PER_PAGE = PER_PAGE_OPTIONS[0];

// what projects/own/with-tasks sorts by, with the direction each field opens in
const PROJECT_SORT_OPTIONS = (t) => [
  { value: 'name', label: t('Name'), defaultOrder: 'asc' },
  { value: 'status', label: t('Status'), defaultOrder: 'asc' },
  { value: 'startDate', label: t('Start Date'), defaultOrder: 'asc' },
  // the nearest deadline and the most open work are what a list is scanned for
  { value: 'deadline', label: t('Deadline'), defaultOrder: 'asc' },
  { value: 'openTaskCount', label: t('Open tasks'), defaultOrder: 'desc' },
  { value: 'createdAt', label: t('Created'), defaultOrder: 'desc' },
];

const PoolingView = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const cookies = new Cookies();
  const userId = Number(cookies.get('userId'));

  // the projects are paginated, so the search belongs to the request - the
  // input is kept apart from it and only lands there once the typing stops
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // null = the default of the API, which is the project number. The sorting
  // and the two view toggles survive a reload.
  const [sort, setSort] = usePersistedState('pooling.sort', {
    sortBy: null,
    sortOrder: 'asc',
  });

  // hideEmpty: projects without a single listed task are noise in a pool,
  // hideDone: a ticked off task leaves the list right away
  const [view, setView] = usePersistedState('pooling.view', {
    hideEmpty: true,
    hideDone: true,
  });

  // the endpoint is scoped to the logged in user, so only status, search and
  // sorting. The page size stays out of it, Pagination appends its own.
  // hideEmpty filters on the server - filtering the page client side would
  // break the pagination.
  const projectsQuery = useMemo(() => {
    const query = buildCapacityProjectsQuery({ search, ...sort });
    return view.hideEmpty ? `${query}&onlyWithTasks=true` : query;
  }, [search, sort, view.hideEmpty]);

  // running projects of the user, each with the tasks assigned to them
  const {
    apiData,
    setApiData,
    isLoading: isProjectsLoading,
  } = useFetch(
    `${OWN_PROJECTS_PATH}?${projectsQuery}&perPage=${PROJECTS_PER_PAGE}`,
  );

  // Pagination drives its own request and expects a loading setter
  const [isPaginating, setIsPaginating] = useState(false);

  // the project filter of the pool offers every project, not just this page
  const { apiData: poolProjectsData } = useFetch(POOL_PROJECTS_PATH);

  // the pool filters server side, so it only reports how much it found
  const [poolCount, setPoolCount] = useState(0);

  // a task freed up on the left re-enters the pool on the right
  const poolReloadRef = useRef(null);

  const projects = useMemo(() => apiData?.data || [], [apiData]);

  // hideDone takes a just ticked off task out of its project right away -
  // hideEmpty lives in the request, so the pagination stays correct
  const visibleProjects = useMemo(
    () =>
      view.hideDone
        ? projects.map((project) => ({
            ...project,
            tasks: (project.tasks || []).filter((task) => !task.status),
          }))
        : projects,
    [projects, view.hideDone],
  );

  // the whole result, not this page
  const projectCount = apiData?.meta?.total ?? projects.length;

  // the overview counts every own task, so a searched total would not match it.
  // The last unsearched total is kept for it.
  const totalProjects = useRef(null);
  useEffect(() => {
    if (!search && apiData) totalProjects.current = projectCount;
  }, [search, apiData, projectCount]);

  // several projects may be open at once
  const [openIds, setOpenIds] = useState([]);

  // phases of every project that has been opened once, so the header numbers
  // survive a collapse and reopening costs no request
  const [phasesByProject, setPhasesByProject] = useState({});

  const [loadingIds, setLoadingIds] = useState([]);
  const [processingTaskIds, setProcessingTaskIds] = useState([]);

  // the dialogs are shared by every project, so they keep the project with them
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });

  // DBRequest reports the API message, which is empty on some endpoints
  function handleFeedback(text, type, fallbackText) {
    setAlertState({
      alertOpen: true,
      alertType: type,
      alertText: text || fallbackText,
    });
  }

  // the task lists inside a project show the tasks of the user only. The
  // page toggles reach into the expanded phases as well.
  const taskFilter = useMemo(
    () => ({
      sortBy: null,
      userIds: userId ? [userId] : [],
      withoutUsers: false,
      search: '',
      hideDone: view.hideDone,
      hideEmpty: view.hideEmpty,
    }),
    [userId, view.hideDone, view.hideEmpty],
  );

  const stats = useMemo(
    () => overviewStats(projects, poolCount),
    [projects, poolCount],
  );

  // the counters of a project row come from the tasks it brought with it
  function projectHeaderStats(project) {
    const taskStats = projectTaskStats(project.tasks);

    return [
      { key: 'open', label: t('Open'), value: taskStats.open },
      {
        key: 'overdue',
        label: t('Overdue'),
        value: taskStats.overdue,
        color: taskStats.overdue ? colors.error : null,
      },
      { key: 'points', label: t('Points'), value: taskStats.openScore },
    ];
  }

  // writes one task back into the project it belongs to
  function applyTask(projectId, task) {
    setApiData((prev) =>
      prev ? addTaskToProject(prev, projectId, task) : prev,
    );
  }

  function handleTaskDelete() {
    const { task, projectId } = taskToDelete;

    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_DELETE(),
      onResponse: () => {
        setApiData((prev) =>
          prev ? removeTaskFromProject(prev, projectId, task.id) : prev,
        );
        setTaskToDelete(null);
      },
      onFeedback: (text, type) => handleFeedback(text, type, t('Task deleted')),
    });
  }

  // changed members can move a task out of the own list and into the pool,
  // so both sides are refreshed
  function handleTaskUsersChange() {
    reloadProjects();
    if (poolReloadRef.current) poolReloadRef.current();
  }

  // reloads the page that is on screen, not the first one
  function reloadProjects() {
    const page = apiData?.meta?.current_page;

    DBRequest({
      config,
      path: `${OWN_PROJECTS_PATH}?${projectsQuery}&perPage=${
        apiData?.meta?.per_page || PROJECTS_PER_PAGE
      }${page > 1 ? `&page=${page}` : ''}`,
      method: API_GET,
      onResponse: setApiData,
    });
  }

  // files, links and comments only replace the task they belong to
  function patchTask(projectId, task, body) {
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_PATCH(body),
      onResponse: (res) => applyTask(projectId, { ...task, ...res.data }),
      onFeedback: (text, type) => handleFeedback(text, type, t('Task updated')),
    });
  }

  // the explorer returns the complete file selection of the task
  function handleTaskFilesChange(task, files, projectId) {
    patchTask(projectId, task, { ...task, files });
  }

  function handleTaskLinkAdd(task, link, projectId) {
    patchTask(projectId, task, {
      externalResources: [...(task.externalResources || []), link],
    });
  }

  // one link is removed by sending the remaining ones
  function handleTaskLinkDelete(task, resource, projectId) {
    patchTask(projectId, task, {
      ...task,
      externalResources: (task.externalResources || []).filter(
        (item) => item.id !== resource.id,
      ),
    });
  }

  // a comment becomes an entry of the task history
  function handleTaskCommentSubmit(task, text, projectId) {
    DBRequest({
      config,
      path: `tasks/${task.id}/history`,
      method: API_POST({ text }),
      onResponse: (res) =>
        applyTask(projectId, {
          ...task,
          history: [res.data, ...(task.history || [])],
        }),
      onFeedback: (message, type) =>
        handleFeedback(message, type, t('Comment added')),
    });
  }

  // a ticked off task is written back into the project it belongs to
  function handleTaskToggle(task, projectId) {
    if (processingTaskIds.includes(task.id)) return;

    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_PATCH({ status: task.status ? 0 : 1 }),
      onLoading: (isProcessing) =>
        setProcessingTaskIds((prev) =>
          isProcessing
            ? [...prev, task.id]
            : prev.filter((id) => id !== task.id),
        ),
      onResponse: (res) => applyTask(projectId, { ...task, ...res.data }),
      onFeedback: (text, type) => handleFeedback(text, type, t('Task updated')),
    });
  }

  const handlePhasesLoaded = useCallback(
    (projectId, phases) =>
      setPhasesByProject((prev) => ({ ...prev, [projectId]: phases })),
    [],
  );

  // a ticked off task only moves a few counters, so they are written into the
  // cached phases instead of loading them again. Not memoized on purpose:
  // reloadProjects reads the page that is on screen right now.
  function handleStatsChange(projectId, workPackageId, delta) {
    setPhasesByProject((prev) =>
      prev[projectId]
        ? {
            ...prev,
            [projectId]: applyTaskStatsDelta(
              prev[projectId],
              workPackageId,
              delta,
            ),
          }
        : prev,
    );

    // the row counters and the closed list read project.tasks, which the delta
    // cannot reach - it names the work package, not the task that changed
    reloadProjects();
  }

  // every open task list reports while it reloads, so one indicator is enough
  const handleListLoading = useCallback(
    (workPackageId, isLoading) =>
      setLoadingIds((prev) => {
        if (isLoading)
          return prev.includes(workPackageId) ? prev : [...prev, workPackageId];
        return prev.filter((id) => id !== workPackageId);
      }),
    [],
  );

  function toggleProject(projectId) {
    setOpenIds((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  }

  // a taken task moves into the project column next to the pool
  const handleTaken = useCallback(
    (task) => {
      const projectId = task.projectId ?? task.project?.id;
      if (!projectId) return;

      setApiData((prev) =>
        prev ? addTaskToProject(prev, projectId, task) : prev,
      );
    },
    [setApiData],
  );

  const isLoadingProjects = (isProjectsLoading && !apiData) || isPaginating;

  // Pagination follows the links of the response, which carry the page only -
  // without the filter appended, turning a page would drop the search
  const paginationData = useMemo(() => {
    if (!apiData?.meta?.links) return apiData;

    // the page size has to travel with the links too, otherwise page two falls
    // back to whatever the API defaults to
    const tail = `${projectsQuery}&perPage=${
      apiData.meta.per_page || PROJECTS_PER_PAGE
    }`;

    return {
      ...apiData,
      meta: {
        ...apiData.meta,
        links: apiData.meta.links.map((link) => ({
          ...link,
          url: link.url ? `${link.url}&${tail}` : null,
        })),
      },
    };
  }, [apiData, projectsQuery]);

  return (
    // flex-1: the scene is a flex column and the header is a sibling
    <Box className="flex flex-col gap-3 flex-1 min-h-0 overflow-hidden pt-2">
      <MyWorkOverview
        stats={stats}
        projectCount={totalProjects.current ?? projectCount}
        userName={cookies.get('position')}
      />

      {/* below lg the pool moves under the projects instead of next to them */}
      <Box className="flex flex-col lg:flex-row gap-3 flex-1 min-h-0 lg:overflow-hidden overflow-y-auto">
        {/* the column itself does not scroll - the header and the pagination
            stay put and only the list between them moves */}
        <Box className="flex flex-col gap-2 shrink-0 lg:flex-1 lg:min-h-0 min-w-0">
          <Box className="flex flex-col gap-2 px-1 shrink-0">
            <Box className="flex items-baseline justify-between gap-2 min-w-0">
              {/* min-w-0 lets the label be cut off instead of pushing the count out */}
              <Typography fontSize="12px" className="opacity-60 min-w-0" noWrap>
                {`${t('My projects')} · ${t('own tasks only')}`}
              </Typography>
              <Typography
                fontSize="12px"
                className="opacity-60 shrink-0"
                noWrap
              >
                {`${projectCount} ${t('Projects')}`}
              </Typography>
            </Box>

            {/* search and sort share a row and wrap onto two on a phone */}
            <Box className="flex flex-wrap items-center gap-2">
              <Box
                className="flex items-center gap-2 rounded-lg px-2 grow"
                // the search keeps a usable width before the sort wraps below it
                sx={{
                  backgroundColor: colors.bgInput,
                  backdropFilter: 'blur(10px)',
                  minWidth: '180px',
                }}
              >
                <SearchIcon sx={{ fontSize: '18px', opacity: 0.5 }} />
                <InputBase
                  fullWidth
                  value={searchInput}
                  placeholder={t('Search project')}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && setSearchInput('')}
                  sx={{ fontSize: '13px', paddingY: '4px' }}
                />
                {Boolean(searchInput) && (
                  <IconButton size="small" onClick={() => setSearchInput('')}>
                    <CloseIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                )}
              </Box>

              <Box className="shrink-0">
                <SortSelect
                  options={PROJECT_SORT_OPTIONS(t)}
                  sortBy={sort.sortBy}
                  sortOrder={sort.sortOrder}
                  onChange={setSort}
                />
              </Box>

              {/* what the page keeps to itself: finished tasks and projects
                  with nothing left to do */}
              <Tooltip title={t('Hide finished tasks')}>
                <ToggleButton
                  size="small"
                  value="hideDone"
                  selected={view.hideDone}
                  onChange={() =>
                    setView((prev) => ({ ...prev, hideDone: !prev.hideDone }))
                  }
                  sx={{ padding: '5px', borderRadius: '8px' }}
                >
                  <RemoveDoneIcon sx={{ fontSize: '16px' }} />
                </ToggleButton>
              </Tooltip>

              <Tooltip title={t('Hide projects without tasks')}>
                <ToggleButton
                  size="small"
                  value="hideEmpty"
                  selected={view.hideEmpty}
                  onChange={() =>
                    setView((prev) => ({ ...prev, hideEmpty: !prev.hideEmpty }))
                  }
                  sx={{ padding: '5px', borderRadius: '8px' }}
                >
                  <VisibilityOffIcon sx={{ fontSize: '16px' }} />
                </ToggleButton>
              </Tooltip>
            </Box>
          </Box>

          {/* below lg the page scrolls as one, so the list only takes its own
              scrollbar in the two column layout */}
          <Box className="flex flex-col gap-2 lg:flex-1 lg:min-h-0 lg:overflow-y-auto pr-1">
            {isLoadingProjects ? (
              <ExoSkeleton n={4} size="70px" />
            ) : visibleProjects.length ? (
              visibleProjects.map((project) => (
                <CapacityProject
                  key={project.id}
                  project={project}
                  phases={phasesByProject[project.id]}
                  isOpen={openIds.includes(project.id)}
                  onToggle={() => toggleProject(project.id)}
                  onPhasesLoaded={handlePhasesLoaded}
                  taskFilter={taskFilter}
                  onLoadingChange={handleListLoading}
                  onStatsChange={handleStatsChange}
                  showHours={false}
                  // the counters below are the own ones, not the whole project
                  showProjectStats={false}
                  extraStats={projectHeaderStats(project)}
                  previewTasks={project.tasks || []}
                  processingTaskIds={processingTaskIds}
                  onTaskToggle={handleTaskToggle}
                  onTaskEdit={(task, projectId) =>
                    setTaskToEdit({ task, projectId })
                  }
                  onTaskDelete={(task, projectId) =>
                    setTaskToDelete({ task, projectId })
                  }
                  // assigning someone else can take the task out of the own list
                  onTaskUsersChange={handleTaskUsersChange}
                  onTaskFilesChange={handleTaskFilesChange}
                  onTaskLinkAdd={handleTaskLinkAdd}
                  onTaskLinkDelete={handleTaskLinkDelete}
                  onTaskCommentSubmit={handleTaskCommentSubmit}
                />
              ))
            ) : (
              <Typography className="opacity-50 py-8 text-center">
                {search
                  ? t('No projects match the search')
                  : t('No projects found')}
              </Typography>
            )}
          </Box>

          {Boolean(apiData?.meta) && (
            <Box
              className="backdrop-blur-lg rounded-lg p-2 shrink-0"
              sx={{ backgroundColor: colors.glass }}
            >
              <Pagination
                data={paginationData}
                setData={setApiData}
                setLoading={setIsPaginating}
                filter={{
                  filter: projectsQuery,
                  order: sort.sortOrder !== 'desc',
                }}
                perPageOptions={PER_PAGE_OPTIONS}
                defaultPerPage={PROJECTS_PER_PAGE}
              />
            </Box>
          )}
        </Box>

        <Box
          className="flex shrink-0 lg:min-h-0"
          sx={{ width: { xs: '100%', lg: '400px', xl: '440px' } }}
        >
          {/* scoped to the user, so it only offers work from their projects */}
          <TaskPool
            usersProjects
            projects={poolProjectsData?.data || []}
            onTaken={handleTaken}
            onTotalChange={setPoolCount}
            onFeedback={handleFeedback}
            reloadRef={poolReloadRef}
          />
        </Box>
      </Box>

      {/* the same dialog the work package view edits a task with */}
      {taskToEdit && (
        <WPEToDoElementPopup
          open={Boolean(taskToEdit)}
          element={taskToEdit.task}
          pid={taskToEdit.projectId}
          wpeId={taskToEdit.task.workPackageListElement?.id}
          onClose={() => setTaskToEdit(null)}
          onResponse={(res) => {
            const updated = { ...taskToEdit.task, ...(res?.data || res) };

            // editing can unassign the user - then the task is no longer
            // theirs and goes back into the pool
            const isStillMember =
              !Array.isArray(updated.users) ||
              updated.users.some((user) => user.id === userId);

            if (isStillMember) {
              applyTask(taskToEdit.projectId, updated);
            } else {
              setApiData((prev) =>
                prev
                  ? removeTaskFromProject(
                      prev,
                      taskToEdit.projectId,
                      taskToEdit.task.id,
                    )
                  : prev,
              );
              if (poolReloadRef.current) poolReloadRef.current();
            }

            setTaskToEdit(null);
          }}
          onDelete={() => {
            setTaskToDelete(taskToEdit);
            setTaskToEdit(null);
          }}
        />
      )}

      <ExoAlert
        open={Boolean(taskToDelete)}
        onClose={() => setTaskToDelete(null)}
        onSubmit={handleTaskDelete}
        alert={{
          submitLabel: t('Delete'),
          submitColor: 'error',
          message: t('Are you sure you want to permanently delete this entry?'),
        }}
      />

      {/* the cards sit in backdrop-blurred boxes, which would pin the fixed
          snackbar to them instead of the viewport */}
      {createPortal(
        <>
          <ExoProcessing open={loadingIds.length > 0} label={t('Loading')} />
          <Feedback state={alertState} setState={setAlertState} />
        </>,
        document.body,
      )}
    </Box>
  );
};

export default PoolingView;
